import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useLiterals from 'utils/hooks/useLiterals';
import {
  ensureModalFields,
  cleanupModalFields,
  cleanupMainFields,
} from 'utils/mercadopagoFields';
import { getMercadoPagoInstance } from 'utils/mercadopago';
import Modal from 'components/Modal';
import Button from 'components/UI/Button';
import Debugger from 'utils/debugger';
import { captureMercadoPagoError } from 'utils/sendSentryLog';

/**
 * Modal para ingresar el CVV de una tarjeta guardada.
 * Usa literals.mercadopago.cvvModal para textos.
 */
const CvvModal = ({
  card = null,
  onClose = () => {},
  onSubmit = () => {},
}) => {
  const literals = useLiterals('mercadopago');
  const cvvModal = literals.cvvModal || {};
  const errors = cvvModal.errors || {};
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cardThumbnail, setCardThumbnail] = useState(null);

  useEffect(() => {
    if (!card || !card.payment_method) return;
    try {
      if (card.payment_method.thumbnail) {
        setCardThumbnail(card.payment_method.thumbnail);
      } else if (card.payment_method.secure_thumbnail) {
        setCardThumbnail(card.payment_method.secure_thumbnail);
      }
    } catch (err) {
      Debugger.error('Error al obtener thumbnail del BIN:', err);
    }
  }, [card]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!mounted) return;
        await ensureModalFields();
      } catch (err) {
        Debugger.error('Error montando campos del modal de MercadoPago:', err);
        captureMercadoPagoError(err, {
          feature: 'CvvModal',
          step: 'mountModalFields',
        });
      }
    })();

    return () => {
      mounted = false;
      cleanupModalFields().catch(() => {});
    };
  }, []);

  const handleSubmit = async () => {
    if (!card) {
      setError(errors.noCard || 'No se ha seleccionado una tarjeta');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Usar la instancia de MercadoPago directamente para crear token con cardId
      const mp = await getMercadoPagoInstance();
      const payload = {
        cardId: card.id || card.card_id,
      };
      const result = await mp.fields.createCardToken(payload);
      const token = result?.id;
      if (!token) {
        throw new Error(result?.message || errors.tokenFailed || 'No se pudo crear el token');
      }

      onSubmit({
        token,
        paymentMethodId: card.payment_method?.id || '',
        cardNumber: `${card.first_six_digits}******${card.last_four_digits}`,
        last_four_digits: card.last_four_digits || '',
        cardholderName: card.cardholder?.name || card.cardholder_name || '',
        payment_type_id: card?.payment_method?.payment_type_id,
      });
    } catch (tokenError) {
      captureMercadoPagoError(tokenError, { feature: 'CvvModal', step: 'tokenization' });
      setError(errors.tokenFailed || 'Error al procesar la tarjeta');
      setLoading(false);
    }
  };

  const cleanupMainMpFields = () => {
    cleanupMainFields().catch(() => {});
  };

  const handleClose = async () => {
    try {
      await cleanupModalFields();
    } catch (cleanupErr) {
      Debugger.error('Error limpiando campos del modal de MercadoPago:', cleanupErr);
      captureMercadoPagoError(cleanupErr, {
        feature: 'CvvModal',
        step: 'cleanupModalFields',
      });
    }
    cleanupMainMpFields();
    onClose();
  };

  return (
    <Modal
      title={cvvModal.title}
      onClose={handleClose}
    >
      <form id='cvv-modal-form-checkout' style={{ display: 'none' }} />
      <div className='cvv-modal-content'>
        {card && (
          <div className='mb-6 text-center'>
            {cardThumbnail ? (
              <img
                src={cardThumbnail}
                alt={card.payment_method?.name || literals.cardSelector?.defaultCardName}
                className='cvv-modal-card-thumb'
              />
            ) : (
              <div className='cvv-modal-card-placeholder' />
            )}
            <p className='cvv-modal-card-label'>{cvvModal.cardLabel}</p>
            <p className='cvv-modal-card-number'>
              {card.payment_method?.name}
              {' '}
              ••••
              {card.last_four_digits}
            </p>
            {card.cardholder?.name && (
              <p className='text-neutral-500 mb-0 cvv-modal-card-holder'>
                {card.cardholder.name}
              </p>
            )}
          </div>
        )}

        <div className='mb-4'>
          <label className='form-label'>{cvvModal.cvvLabel}</label>
          <div
            id='cvv-modal__securityCode'
            className='mp-field-container'
          />
          <small className='text-neutral-500'>{cvvModal.securityCodeHint}</small>
        </div>

        <div className='flex flex-col-reverse md:flex-row gap-2'>
          <Button
            text={literals.common?.cancel || cvvModal.cancel}
            type='secondary'
            className='flex-1'
            onClick={handleClose}
            disabled={loading}
          />
          <Button
            text={cvvModal.submitButton}
            type='primary'
            className='flex-1'
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          />
        </div>
        {error && (
          <div className='mt-4'>
            <div className='alert alert-danger'>{error}</div>
          </div>
        )}
      </div>
    </Modal>
  );
};

CvvModal.propTypes = {
  card: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default CvvModal;
